import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface PersonnelRHDTO {
  id?: number;
  userId: number;
  userNom?: string;
  userPrenom?: string;
  userContact?: string;
  userEmail?: string;
  roleNom?: string;
  typeContrat: string;
  modeRemuneration: string;
  salaireBase?: number;
  tarifParLivraison?: number;
  dateEmbauche: string;
  dateFin?: string;
  poste?: string;
  numeroMatricule?: string;
  responsableId?: number;
  responsableNom?: string;
  notes?: string;
  pointsCumules?: number;
  actif?: boolean;
  dateCreation?: string;
}

export interface PrimeMensuelleDTO {
  id: number;
  personnelId: number;
  personnelNom: string;
  personnelPrenom: string;
  personnelMatricule?: string;
  moisAnnee: string;
  nbLivraisons: number;
  totalPoints: number;
  montantBase: number;
  montantPrime: number;
  statut: string;
  dateValidation?: string;
  datePayement?: string;
}

export interface ReleveLivreurDTO {
  personnelId: number;
  personnelNom: string;
  personnelPrenom: string;
  moisAnnee: string;
  nbLivraisons: number;
  totalPoints: number;
  montantBase?: number;
  montantPrime?: number;
  details: any[];
}

export interface ConfigPointsDTO {
  id?: number;
  typeEnvoi: string;
  nbPointsParLivraison: number;
  valeurPointEnFCFA: number;
  description?: string;
  actif?: boolean;
}

export interface ConfigRappelDTO {
  id?: number;
  typeDocument: string;
  delaiRappel1Jours: number;
  delaiRappel2Jours: number;
  delaiRappel3Jours: number;
  emailsCibles?: string;
  actif?: boolean;
}

@Injectable({ providedIn: 'root' })
export class PersonnelRhService {
  private api = environment.apiurl + 'rh';

  constructor(private http: HttpClient) {}

  // Personnel
  getAllPersonnel(): Observable<PersonnelRHDTO[]> { return this.http.get<PersonnelRHDTO[]>(`${this.api}/personnel`); }
  getAllPersonnelInclInactifs(): Observable<PersonnelRHDTO[]> { return this.http.get<PersonnelRHDTO[]>(`${this.api}/personnel?actifSeulement=false`); }
  getPersonnelById(id: number): Observable<PersonnelRHDTO> { return this.http.get<PersonnelRHDTO>(`${this.api}/personnel/${id}`); }
  getPersonnelByUserId(userId: number): Observable<PersonnelRHDTO> { return this.http.get<PersonnelRHDTO>(`${this.api}/personnel/user/${userId}`); }
  creerPersonnel(dto: PersonnelRHDTO): Observable<PersonnelRHDTO> { return this.http.post<PersonnelRHDTO>(`${this.api}/personnel`, dto); }
  modifierPersonnel(id: number, dto: PersonnelRHDTO): Observable<PersonnelRHDTO> { return this.http.put<PersonnelRHDTO>(`${this.api}/personnel/${id}`, dto); }
  toggleActif(id: number): Observable<PersonnelRHDTO> { return this.http.put<PersonnelRHDTO>(`${this.api}/personnel/${id}/toggleActif`, {}); }
  getReleve(personnelId: number, moisAnnee: string): Observable<ReleveLivreurDTO> { return this.http.get<ReleveLivreurDTO>(`${this.api}/personnel/${personnelId}/releve/${moisAnnee}`); }
  getHistoriquePrimes(personnelId: number): Observable<PrimeMensuelleDTO[]> { return this.http.get<PrimeMensuelleDTO[]>(`${this.api}/personnel/${personnelId}/primes`); }
  getHistoriquePrimesParUser(userId: number): Observable<PrimeMensuelleDTO[]> { return this.http.get<PrimeMensuelleDTO[]>(`${this.api}/personnel/user/${userId}/primes`); }

  // Primes
  getPrimes(moisAnnee?: string): Observable<PrimeMensuelleDTO[]> {
    const params = moisAnnee ? `?moisAnnee=${moisAnnee}` : '';
    return this.http.get<PrimeMensuelleDTO[]>(`${this.api}/primes${params}`);
  }
  genererPrime(personnelId: number, moisAnnee: string): Observable<PrimeMensuelleDTO> {
    return this.http.post<PrimeMensuelleDTO>(`${this.api}/primes/generer/${personnelId}/${moisAnnee}`, {});
  }
  genererPrimesMois(moisAnnee: string): Observable<any> {
    return this.http.post<any>(`${this.api}/primes/generer-mois/${moisAnnee}`, {});
  }
  validerPrime(primeId: number, valideParId: number): Observable<PrimeMensuelleDTO> {
    return this.http.put<PrimeMensuelleDTO>(`${this.api}/primes/${primeId}/valider?valideParId=${valideParId}`, {});
  }
  marquerPayee(primeId: number): Observable<PrimeMensuelleDTO> {
    return this.http.put<PrimeMensuelleDTO>(`${this.api}/primes/${primeId}/payer`, {});
  }

  // Config
  getConfigPoints(): Observable<ConfigPointsDTO[]> { return this.http.get<ConfigPointsDTO[]>(`${this.api}/config/points`); }
  saveConfigPoints(cfg: ConfigPointsDTO): Observable<ConfigPointsDTO> { return this.http.post<ConfigPointsDTO>(`${this.api}/config/points`, cfg); }
  getConfigRappels(): Observable<ConfigRappelDTO[]> { return this.http.get<ConfigRappelDTO[]>(`${this.api}/config/rappels`); }
  saveConfigRappel(cfg: ConfigRappelDTO): Observable<ConfigRappelDTO> { return this.http.post<ConfigRappelDTO>(`${this.api}/config/rappels`, cfg); }
}
